// src/components/TenantUserManagement.js
import React, { useState, useEffect, useMemo } from "react";
import { Plus, User, Shield, Clock, Mail, ArrowLeft } from "react-feather";
import UserRolesModal from "./UserRolesModal";
import InviteTenantUserModal from "./InviteTenantUserModal";
import { useApi } from "../hooks/useApi";
import { fetchAllTenantUserRoles, fetchTenantUsers, fetchTenantPendingInvites } from "../services/userRolesService";
import Button from "../components/Buttons";
import Card from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorBoundary from "../components/ErrorBoundary";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import { useNavigate } from "react-router-dom";

const roleColors = {
  OWNER: 'bg-red-100 text-red-800',
  ADMIN: 'bg-purple-100 text-purple-800',
  RISK_MANAGER: 'bg-orange-100 text-orange-800',
  CONTRIBUTOR: 'bg-blue-100 text-blue-800',
  APPROVER: 'bg-green-100 text-green-800',
  VIEWER: 'bg-gray-100 text-gray-800',
};

const roleDisplayNames = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  RISK_MANAGER: 'Risk Manager',
  CONTRIBUTOR: 'Contributor',
  APPROVER: 'Approver',
  VIEWER: 'Viewer',
};

const TenantUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [userRolesMap, setUserRolesMap] = useState({});
  const [pendingInvites, setPendingInvites] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();
  const { hasMinimumRoleAtOrAbove } = useRBAC();
  const apiCall = useApi();
  const navigate = useNavigate();

  // Derive tenants the current user can administer from orgHierarchy
  const adminTenants = useMemo(() => {
    try {
      const orgHierarchy = JSON.parse(localStorage.getItem("orgHierarchy") || "[]");
      const tenantMap = new Map();
      orgHierarchy.forEach(h => {
        if (h.tenantId && !tenantMap.has(h.tenantId)) {
          tenantMap.set(h.tenantId, {
            tenantId: h.tenantId,
            tenantName: h.tenantName || `Tenant ${h.tenantId}`,
          });
        }
      });
      // Filter to tenants where user has ADMIN+ role
      return [...tenantMap.values()].filter(t =>
        hasMinimumRoleAtOrAbove('ADMIN', 'TENANT', t.tenantId)
      );
    } catch (e) {
      return [];
    }
  }, [hasMinimumRoleAtOrAbove]);

  // Selected tenant state — initialize from localStorage or auto-select
  const [selectedTenantId, setSelectedTenantId] = useState(() => {
    const stored = localStorage.getItem("selectedTenantId");
    if (stored) {
      const parsed = parseInt(stored);
      // Will be validated against adminTenants in useEffect below
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  });

  // Auto-select tenant if only one, or validate stored selection
  useEffect(() => {
    if (adminTenants.length === 0) return;

    if (selectedTenantId && adminTenants.some(t => t.tenantId === selectedTenantId)) {
      // Current selection is valid
      return;
    }

    // Auto-select if only one, or pick first
    const autoId = adminTenants[0].tenantId;
    setSelectedTenantId(autoId);
    localStorage.setItem("selectedTenantId", autoId.toString());
  }, [adminTenants, selectedTenantId]);

  // Sync selectedTenantId to localStorage
  const handleTenantChange = (e) => {
    const newTenantId = parseInt(e.target.value);
    setSelectedTenantId(newTenantId);
    localStorage.setItem("selectedTenantId", newTenantId.toString());
  };

  // RBAC guard: redirect if not admin of any tenant
  const isTenantAdmin = selectedTenantId
    ? hasMinimumRoleAtOrAbove('ADMIN', 'TENANT', selectedTenantId)
    : false;

  useEffect(() => {
    if (adminTenants.length === 0 && !loading) {
      navigate('/organizations');
    }
  }, [adminTenants, loading, navigate]);

  const fetchUsers = async () => {
    try {
      const data = await fetchTenantUsers(apiCall);
      setUsers(data || []);
    } catch (err) {} finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const allRoles = await fetchAllTenantUserRoles(apiCall);
      const rolesMap = {};
      (allRoles || []).forEach(role => {
        if (!rolesMap[role.userId]) {
          rolesMap[role.userId] = {
            roles: [],
            highestRole: null,
            highestRoleValue: Infinity,
            count: 0,
          };
        }
        rolesMap[role.userId].roles.push(role);
        rolesMap[role.userId].count++;
        if (role.roleValue < rolesMap[role.userId].highestRoleValue) {
          // OWNER is provisioning-only; surface it as ADMIN in the UI.
          rolesMap[role.userId].highestRole = role.roleValue === 1 ? 'ADMIN' : role.role;
          rolesMap[role.userId].highestRoleValue = role.roleValue;
        }
      });
      setUserRolesMap(rolesMap);
    } catch (err) {}
  };

  const fetchPendingInvites = async () => {
    try {
      const data = await fetchTenantPendingInvites(apiCall);
      setPendingInvites(data || []);
    } catch (err) {}
  };

  // Re-fetch when token or selectedTenantId changes
  useEffect(() => {
    if (selectedTenantId) {
      setLoading(true);
      fetchUsers();
      fetchRoles();
      fetchPendingInvites();
    }
  }, [token, selectedTenantId]);

  if (loading) return <LoadingSpinner />;
  if (!isTenantAdmin) return null;

  const tabs = [
    { id: 'active', label: 'Active Users', icon: '👤', count: users.length },
    { id: 'pending', label: 'Pending Invitations', icon: '📧', count: pendingInvites.length },
  ];

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-theme-primary">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <button
              onClick={() => { window.location.href = '/organizations'; }}
              className="flex items-center gap-1 text-sm text-content-secondary hover:text-content-primary mb-4 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-content-primary">Tenant User Management</h1>
                <p className="mt-2 text-gray-600">Manage users and roles across all organizations in your tenant</p>
              </div>
              <div className="flex items-center gap-4">
                {adminTenants.length > 1 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Tenant:</label>
                    <select
                      value={selectedTenantId || ''}
                      onChange={handleTenantChange}
                      className="block w-auto px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {adminTenants.map(t => (
                        <option key={t.tenantId} value={t.tenantId}>
                          {t.tenantName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <Button onClick={() => setShowAddUserModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </div>
            </div>
          </div>

          <Card>
            {/* Chrome tab bar */}
            <div className="flex items-end gap-0.5 bg-slate-100 border border-gray-200 border-b-0 rounded-t-xl px-2 pt-2 -mx-6 -mt-6 mb-0">
              {tabs.map(t => {
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                      isActive
                        ? 'bg-white text-indigo-700 border-gray-200 z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                        : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                    }`}
                    style={isActive ? { borderBottomColor: '#ffffff' } : {}}
                  >
                    <span className="text-sm leading-none">{t.icon}</span>
                    <span>{t.label}</span>
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                      isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {t.count}
                    </span>
                  </button>
                );
              })}
              <div className="flex-1 border-b border-gray-200 self-end h-[1px]" />
            </div>

            {/* Tab content */}
            <div className="pt-4">
              {/* Tab 1: Active Users */}
              {activeTab === 'active' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((u) => (
                        <tr key={u.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-content-primary">{u.fullName}</div>
                                <div className="text-sm text-gray-500">{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {userRolesMap[u.id] ? (
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[userRolesMap[u.id].highestRole] || 'bg-blue-100 text-blue-800'}`}>
                                  {roleDisplayNames[userRolesMap[u.id].highestRole] || userRolesMap[u.id].highestRole}
                                </span>
                                {userRolesMap[u.id].count > 1 && (
                                  <span className="text-xs text-gray-500">
                                    +{userRolesMap[u.id].count - 1} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                                No roles
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => {
                                setSelectedUser(u);
                                setShowRolesModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                            >
                              <Shield className="h-4 w-4 mr-1" />
                              Edit Roles
                            </button>
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-6 py-10 text-center text-gray-500 text-sm">
                            No users found in this tenant.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tab 2: Pending Invitations */}
              {activeTab === 'pending' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invitee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invited On</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingInvites.map((inv) => (
                        <tr key={inv.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <Mail className="h-5 w-5 text-amber-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-content-primary">{inv.fullName}</div>
                                <div className="text-sm text-gray-500">{inv.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[inv.role] || 'bg-gray-100 text-gray-600'}`}>
                              {inv.roleDisplayName || roleDisplayNames[inv.role] || inv.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-gray-400" />
                              {new Date(inv.invitedAt).toLocaleDateString(undefined, {
                                year: 'numeric', month: 'short', day: 'numeric',
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {new Date(inv.expiresAt).toLocaleDateString(undefined, {
                              year: 'numeric', month: 'short', day: 'numeric',
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {inv.isExpired ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                Expired
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                Pending
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {pendingInvites.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-6 py-10 text-center text-gray-500 text-sm">
                            No pending invitations.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        </div>

        <InviteTenantUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSuccess={fetchPendingInvites}
        />

        {showRolesModal && selectedUser && (
          <UserRolesModal
            isOpen={showRolesModal}
            onClose={() => {
              setShowRolesModal(false);
              setSelectedUser(null);
            }}
            user={selectedUser}
            tenantMode={true}
            tenantId={selectedTenantId}
            onSaved={() => {
              fetchUsers();
              fetchRoles();
              setShowRolesModal(false);
              setSelectedUser(null);
            }}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default TenantUserManagement;
